import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

class EchtPBOptionSheet extends Component {
  #observer;
  #abort;
  #media;
  #dialog;
  #select;
  #trigger;
  #queued = false;
  #menu;
  #menuAbort;
  #activeIndex = -1;
  #search = '';
  #searchTime = 0;
  #lastFocused;
  #repair = false;
  #auto = false;
  #note;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#media = matchMedia('(max-width: 749px)');
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('change', this.#sync, { signal });
    this.addEventListener('change', this.#track, { signal });
    this.addEventListener('focusin', this.#focusIn, { signal });
    this.addEventListener('focusout', this.#focusOut, { signal });
    this.addEventListener('keydown', this.#desktopKey, { signal });
    this.#media.addEventListener('change', this.#resize, { signal });
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => {
      if (this.#queued) return;
      this.#queued = true;
      queueMicrotask(() => {
        this.#queued = false;
        if (this.isConnected) this.#sync();
      });
    });
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  #sync = () => {
    if (this.#select && !this.contains(this.#select)) {
      this.#close(false, true);
      this.#closeDesktop(false);
    }
    let rerendered = false;
    for (const select of this.querySelectorAll('select.variant-option__select')) {
      const wrapper = select.closest('.variant-option__select-wrapper');
      if (!wrapper || !select.id) continue;
      let trigger = wrapper.querySelector('.echt-pb-option-sheet__trigger');
      if (!trigger) {
        rerendered = true;
        trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'variant-option__select echt-pb-option-sheet__trigger';
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');
        wrapper.append(trigger);
      }
      trigger.dataset.selectId = select.id;
      trigger.setAttribute('aria-haspopup', this.#media.matches ? 'dialog' : 'listbox');
      if (this.#media.matches) trigger.removeAttribute('role');
      else trigger.setAttribute('role', 'combobox');
      const enhanced = this.#media.matches || typeof HTMLElement.prototype.showPopover === 'function';
      if (enhanced) select.setAttribute('aria-hidden', 'true');
      else select.removeAttribute('aria-hidden');
      select.tabIndex = enhanced ? -1 : 0;
      const label = select.labels?.[0]?.textContent.trim() || 'Choose option';
      const value = select.selectedOptions[0]?.textContent.trim() || 'Select';
      if (trigger.textContent !== value) trigger.textContent = value;
      trigger.setAttribute('aria-label', `${label}: ${value}`);
      trigger.disabled = select.disabled;
      wrapper.toggleAttribute('data-sheet-ready', enhanced);
    }
    // The stock variant-picker re-render drops the injected triggers, so new ones were just made.
    if (!rerendered) return;
    this.#rebind();
    this.#restoreFocus();
    if (this.#repair) this.#repairSizes();
  };

  #triggerFor(select) {
    return select?.closest('.variant-option__select-wrapper')?.querySelector('.echt-pb-option-sheet__trigger');
  }

  #focusIn = (event) => {
    this.#lastFocused = event.target.closest?.('.echt-pb-option-sheet__trigger') || undefined;
  };

  #focusOut = (event) => {
    const trigger = event.target;
    if (trigger !== this.#lastFocused) return;
    // A trigger removed by a re-render also blurs; only forget one that is still on the page.
    queueMicrotask(() => {
      if (trigger === this.#lastFocused && trigger.isConnected) this.#lastFocused = undefined;
    });
  };

  // Keeps an open menu or sheet attached to the trigger that replaced its own.
  #rebind() {
    if (!this.#trigger || this.#trigger.isConnected) return;
    const trigger = this.#triggerFor(this.#select);
    if (!trigger) return;
    this.#trigger = trigger;
    if (this.#menu) {
      const active = this.#activeIndex;
      this.#fillMenu();
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-controls', this.#menu.id);
      trigger.focus({ preventScroll: true });
      this.#positionDesktop();
      this.#activate(active, false);
    } else if (this.#dialog) {
      trigger.setAttribute('aria-expanded', 'true');
      if (!this.#dialog.hasAttribute('data-closing')) this.#fillSheet();
    }
  }

  #restoreFocus() {
    const lost = this.#lastFocused;
    if (!lost || lost.isConnected) return;
    this.#lastFocused = undefined;
    if (document.activeElement && document.activeElement !== document.body) return;
    this.#triggerFor(document.getElementById(lost.dataset.selectId))?.focus({ preventScroll: true });
  }

  #track = (event) => {
    if (this.#auto || !(event.target instanceof HTMLSelectElement)) return;
    // Only a box change can strand the kept sizes; a size the shopper picks is left alone.
    this.#repair = event.target === this.querySelector('select.variant-option__select');
    this.#setNote('');
  };

  // After a box change, moves a size the new box does not offer to the nearest one it does.
  // One select per re-render: the server marks each option against the selections before it.
  #repairSizes() {
    const [box, ...sizes] = this.querySelectorAll('select.variant-option__select');
    const unavailable = option => option.dataset.optionAvailable === 'false' || option.disabled;
    const name = option => option.dataset.optionLabel || option.textContent.trim();
    for (const select of sizes) {
      const from = select.selectedIndex;
      const current = select.options[from];
      if (!current || !unavailable(current)) continue;
      let best = -1;
      [...select.options].forEach((option, index) => {
        if (unavailable(option)) return;
        if (best < 0 || Math.abs(index - from) <= Math.abs(best - from)) best = index;
      });
      if (best < 0) break;
      select.selectedIndex = best;
      const label = select.labels?.[0]?.textContent.trim() || 'Size';
      const sentence = `${label} changed to ${name(select.options[best])}. ${name(current)} isn’t available in the ${name(box.selectedOptions[0])} box.`;
      this.#setNote([this.#note?.textContent, sentence].filter(Boolean).join(' '));
      this.#auto = true;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.#auto = false;
      return;
    }
    this.#repair = false;
  }

  #setNote(text) {
    if (!text && !this.#note) return;
    if (!this.#note?.isConnected) {
      this.#note = document.createElement('p');
      this.#note.className = 'echt-pb-option-sheet__note';
      this.#note.setAttribute('role', 'status');
      this.append(this.#note);
    }
    this.#note.textContent = text;
    this.#note.hidden = !text;
  }

  #click = (event) => {
    const trigger = event.target.closest('.echt-pb-option-sheet__trigger');
    if (!trigger || !this.contains(trigger)) return;
    const select = document.getElementById(trigger.dataset.selectId);
    if (!(select instanceof HTMLSelectElement) || !this.contains(select)) return;
    if (this.#media.matches) this.#open(select, trigger);
    else if (this.#menu && this.#trigger === trigger) this.#closeDesktop();
    else this.#openDesktop(select, trigger);
  };

  #openDesktop(select, trigger) {
    if (typeof HTMLElement.prototype.showPopover !== 'function') return;
    this.#close(false, true);
    this.#closeDesktop(false);
    this.#select = select;
    this.#trigger = trigger;
    this.#search = '';
    this.#activeIndex = -1;
    const menu = document.createElement('div');
    menu.className = 'echt-pb-variant-picker echt-pb-option-sheet__menu';
    menu.id = `${select.id}-pb-listbox`;
    menu.setAttribute('popover', 'manual');
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', select.labels?.[0]?.textContent.trim() || 'Choose option');
    this.#menu = menu;
    this.#fillMenu();
    document.body.append(menu);
    menu.showPopover();
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-controls', menu.id);
    trigger.focus({ preventScroll: true });
    this.#positionDesktop();
    const selected = select.options[select.selectedIndex];
    const initialIndex = selected && !selected.disabled && selected.parentElement?.disabled !== true
      ? select.selectedIndex
      : [...select.options].findIndex(option => !option.disabled && option.parentElement?.disabled !== true);
    this.#activate(initialIndex);
    this.#menuAbort = new AbortController();
    const { signal } = this.#menuAbort;
    document.addEventListener('pointerdown', event => {
      if (!menu.contains(event.target) && !this.#trigger?.contains(event.target)) this.#closeDesktop(false);
    }, { capture: true, signal });
    document.addEventListener('focusin', event => {
      if (!menu.contains(event.target) && !this.#trigger?.contains(event.target)) this.#closeDesktop(false);
    }, { signal });
    window.addEventListener('resize', () => this.#closeDesktop(), { signal });
    // Follow the field while the page scrolls; only a field scrolled out of view closes the menu.
    document.addEventListener('scroll', event => {
      if (menu.contains(event.target)) return;
      const rect = this.#trigger?.getBoundingClientRect();
      if (!rect || rect.bottom < 0 || rect.top > innerHeight) this.#closeDesktop(false);
      else this.#positionDesktop();
    }, { capture: true, signal });
  }

  #fillMenu() {
    const menu = this.#menu;
    const select = this.#select;
    menu.replaceChildren();
    [...select.options].forEach((option, index) => {
      const row = document.createElement('div');
      row.className = 'echt-pb-option-sheet__desktop-row';
      row.id = `${menu.id}-${index}`;
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', String(option.selected));
      row.setAttribute('aria-disabled', String(option.disabled || option.parentElement?.disabled === true));
      row.textContent = option.textContent.trim();
      if (option.selected) {
        const check = document.createElement('span');
        check.textContent = '✓';
        check.setAttribute('aria-hidden', 'true');
        row.append(check);
      }
      row.addEventListener('pointerdown', event => event.preventDefault());
      row.addEventListener('pointermove', () => {
        menu.removeAttribute('data-keyboard');
        this.#activate(index, false);
      });
      row.addEventListener('click', () => this.#chooseDesktop(index));
      menu.append(row);
    });
  }

  #positionDesktop() {
    const rect = this.#trigger.parentElement.getBoundingClientRect();
    const menu = this.#menu;
    const gutter = 8;
    const below = innerHeight - rect.bottom - gutter;
    const above = rect.top - gutter;
    const flip = below < Math.min(menu.scrollHeight, 240) && above > below;
    const room = flip ? above : below;
    menu.style.width = `${Math.min(rect.width, innerWidth - gutter * 2)}px`;
    menu.style.maxHeight = `${Math.max(0, Math.min(room - 4, innerHeight / 2))}px`;
    menu.style.left = `${Math.max(gutter, Math.min(rect.left, innerWidth - menu.offsetWidth - gutter))}px`;
    menu.style.top = `${flip ? rect.top - menu.offsetHeight - 4 : rect.bottom + 4}px`;
  }

  #activate(index, scroll = true) {
    const rows = [...this.#menu?.children || []];
    const row = rows[index];
    if (!row || row.getAttribute('aria-disabled') === 'true') return;
    this.#activeIndex = index;
    rows.forEach((item, i) => item.toggleAttribute('data-active', i === index));
    this.#trigger.setAttribute('aria-activedescendant', row.id);
    if (scroll) row.scrollIntoView({ block: 'nearest' });
  }

  #chooseDesktop(index) {
    const select = this.#select;
    const option = select?.options[index];
    if (!option || !this.contains(select) || option.disabled || option.parentElement?.disabled === true) return;
    const changed = select.selectedIndex !== index;
    select.selectedIndex = index;
    this.#closeDesktop();
    this.#sync();
    if (changed) select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #desktopKey = (event) => {
    if (this.#media.matches || event.ctrlKey || event.metaKey || event.altKey) return;
    const trigger = event.target.closest('.echt-pb-option-sheet__trigger');
    if (!trigger) return;
    if (event.key === 'Escape' && this.#menu) {
      event.preventDefault();
      this.#closeDesktop();
      return;
    }
    if (event.key === 'Tab') {
      this.#closeDesktop(false);
      return;
    }
    const navigation = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    const selection = ['Enter', ' '];
    const typing = event.key.length === 1 && event.key !== ' ';
    if (!navigation.includes(event.key) && !selection.includes(event.key) && !typing) return;
    event.preventDefault();
    if (!this.#menu) {
      const select = document.getElementById(trigger.dataset.selectId);
      this.#openDesktop(select, trigger);
      if (this.#menu) this.#menu.dataset.keyboard = '';
      if (!this.#menu || selection.includes(event.key)) return;
    } else if (selection.includes(event.key)) {
      this.#chooseDesktop(this.#activeIndex);
      return;
    }
    this.#menu.dataset.keyboard = '';
    const enabled = [...this.#select.options].map((option, i) => ({ option, i }))
      .filter(({ option }) => !option.disabled && option.parentElement?.disabled !== true);
    if (!enabled.length) return;
    let position = enabled.findIndex(({ i }) => i === this.#activeIndex);
    if (event.key === 'Home') position = 0;
    if (event.key === 'End') position = enabled.length - 1;
    if (event.key === 'ArrowDown') position = Math.min(enabled.length - 1, position + 1);
    if (event.key === 'ArrowUp') position = Math.max(0, position - 1);
    if (typing) {
      const now = performance.now();
      this.#search = now - this.#searchTime > 700 ? event.key : this.#search + event.key;
      this.#searchTime = now;
      const query = this.#search.toLocaleLowerCase();
      const match = enabled.find(({ option }) => option.textContent.trim().toLocaleLowerCase().startsWith(query));
      if (match) this.#activate(match.i);
      return;
    }
    this.#activate(enabled[position].i);
  };

  #closeDesktop(restoreFocus = true) {
    if (!this.#menu) return;
    this.#menuAbort?.abort();
    this.#menu.hidePopover();
    this.#menu.remove();
    this.#menu = undefined;
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#trigger?.removeAttribute('aria-controls');
    this.#trigger?.removeAttribute('aria-activedescendant');
    if (restoreFocus && this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: true });
    this.#select = this.#trigger = undefined;
    this.#activeIndex = -1;
  }

  #open(select, trigger) {
    this.#closeDesktop(false);
    this.#close(false, true);
    this.#select = select;
    this.#trigger = trigger;
    const dialog = document.createElement('dialog');
    dialog.className = 'echt-pb-variant-picker echt-pb-option-sheet__dialog';
    const label = select.labels?.[0]?.textContent.trim() || 'Choose option';
    dialog.setAttribute('aria-label', label);
    const header = document.createElement('div');
    header.className = 'echt-pb-option-sheet__header';
    const title = document.createElement('h2');
    title.className = 'echt-pb-option-sheet__title';
    title.textContent = label;
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'echt-pb-option-sheet__cancel';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', () => this.#close());
    header.append(title, cancel);
    dialog.append(header);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#close();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientY > rect.bottom || event.clientX < rect.left || event.clientX > rect.right) this.#close();
    });
    this.#dialog = dialog;
    this.#fillSheet();
    document.body.append(dialog);
    lockScroll(dialog);
    trigger.setAttribute('aria-expanded', 'true');
    dialog.showModal();
    cancel.focus({ preventScroll: true });
  }

  #fillSheet() {
    const dialog = this.#dialog;
    const select = this.#select;
    const stale = [...dialog.querySelectorAll('.echt-pb-option-sheet__row')];
    const focused = stale.indexOf(document.activeElement);
    stale.forEach(row => row.remove());
    [...select.options].forEach((option, index) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'echt-pb-option-sheet__row';
      row.textContent = option.textContent.trim();
      row.disabled = option.disabled || option.parentElement?.disabled === true;
      row.setAttribute('aria-pressed', String(option.selected));
      if (option.selected) {
        const check = document.createElement('span');
        check.textContent = '✓';
        check.setAttribute('aria-hidden', 'true');
        row.append(check);
      }
      row.addEventListener('click', () => {
        if (dialog.hasAttribute('data-closing') || !this.contains(select) || !select.options[index] || row.disabled) return;
        const changed = select.selectedIndex !== index;
        select.selectedIndex = index;
        this.#close();
        this.#sync();
        if (changed) select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      dialog.append(row);
    });
    if (focused >= 0) dialog.querySelectorAll('.echt-pb-option-sheet__row')[focused]?.focus({ preventScroll: true });
  }

  #resize = () => {
    this.#closeDesktop();
    if (!this.#media.matches) this.#close(true, true);
    this.#sync();
  };

  #close(restoreFocus = true, immediate = false) {
    const dialog = this.#dialog;
    if (!dialog) return;
    const finish = () => {
      // An older animation must never close a newly opened sheet.
      if (this.#dialog !== dialog) return;
      dialog.close();
      unlockScroll(dialog);
      dialog.remove();
      this.#dialog = undefined;
      this.#trigger?.setAttribute('aria-expanded', 'false');
      const focusTarget = this.#media.matches || typeof HTMLElement.prototype.showPopover === 'function' ? this.#trigger : this.#select;
      if (restoreFocus && focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
      this.#select = this.#trigger = undefined;
    };
    if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches || document.querySelector('[data-motion-paused]')) {
      finish();
      return;
    }
    if (dialog.hasAttribute('data-closing')) return;
    dialog.dataset.closing = '';
    const animations = dialog.getAnimations();
    if (!animations.length) finish();
    else {
      // Animations pause in a hidden tab; never leave the sheet and scroll lock waiting on one.
      const timeout = new Promise(resolve => setTimeout(resolve, 400));
      Promise.race([Promise.allSettled(animations.map(animation => animation.finished)), timeout]).then(finish);
    }
  }

  disconnectedCallback() {
    this.#closeDesktop(false);
    this.#close(false, true);
    this.#abort?.abort();
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }
}

if (!customElements.get('echt-pb-option-sheet')) {
  customElements.define('echt-pb-option-sheet', EchtPBOptionSheet);
}
